import { mergeAll } from 'lodash/fp'

import { DEFAULT_THEME } from '../defaultTheme'
import { TTheme } from '../typings'
import { ensureElementStateColors } from './ensureElementStateColors'

type TThemeConfigBaseColors = {
  companyPrimary: string
  conversion: string
}

type TOptions = {
  /**
   * simple company setting used to customize the app a bit with low cost/effort
   * roughly resembles the FCompanyStyles.colors shape
   */
  baseColors: TThemeConfigBaseColors
  /**
   * Theme-shaped company setting used to customize any BG color in the app,
   * used by our designers to bring the app fully in-line with clients visual style
   */
  advancedColors?: Nullable<RecursivePartial<TTheme>>
  /**
   * Theme overrides that the _user_ has stored (persisted) locally,
   * used by our designers to test & prepare color combinations that can then be used as
   * advancedColors for some company
   */
  overrideColors?: Nullable<RecursivePartial<TTheme>>
}

/**
 * returns theme built based on provided company-related color settings
 * with default theme as fallback values
 *
 * build priority:
 *  1. overrideColors (used for local testing & setting up)
 *  2. advancedColors (company setting used to override possibly any color in the Theme)
 *  3. baseColors (simple company setting made of few colors that are re-used on multiple places in the Theme shape)
 *  4. DEFAULT_THEME (static Theme object defined in this codebase)
 *
 * at least the baseColors setting should always be defined for a company
 */
export const buildTheme = ({
  baseColors,
  advancedColors = {},
  overrideColors = {},
}: TOptions): TTheme => {
  const baseColorsTheme = buildBaseTheme(baseColors)

  return mergeAll([DEFAULT_THEME, baseColorsTheme, advancedColors, overrideColors])
}

type TCreateBaseThemeOptions = Partial<TThemeConfigBaseColors>

/**
 * builds Theme-shaped object from baseColors
 * – this marks where the base colors are used in the Theme
 *
 * ! if you add a new color here, make sure to add it to the `useFgColorValue.ts` hook as well,
 * as it is used to determine fg contrast
 */
export const buildBaseTheme = ({
  conversion,
  companyPrimary,
}: TCreateBaseThemeOptions): RecursivePartial<TTheme> => {
  // build all theme parts that depend on conversion color
  const conversionColorTheme: RecursivePartial<TTheme> =
    conversion !== undefined
      ? {
          activeColors: {
            conversion: ensureElementStateColors({
              color: conversion,
              defaultDisabledColor: DEFAULT_THEME.specialColors.disabled,
            }),
          },
        }
      : {}

  // build all theme parts that depend on companyPrimary color
  const companyColorTheme: RecursivePartial<TTheme> =
    companyPrimary !== undefined
      ? {
          bgColors: {
            company: companyPrimary,
          },
          activeColors: {
            primary: ensureElementStateColors({
              color: companyPrimary,
              defaultDisabledColor: DEFAULT_THEME.specialColors.disabled,
            }),
          },
          specialColors: {
            mapElementHighlighted: companyPrimary,
            illustration: companyPrimary,
            company: companyPrimary,
          },
        }
      : {}

  return mergeAll([companyColorTheme, conversionColorTheme])
}
